import React from 'react';
import styled, { css } from 'styled-components';
import { primitiveColors } from '@/styles/template';
import Plus from '../../../../assets/icons/plus.svg?react';
import Minus from '../../../../assets/icons/minus.svg?react';
import Next from '../../../../assets/icons/next.svg?react';
import Back from '../../../../assets/icons/back.svg?react';
import Open from '../../../../assets/icons/open.svg?react';
import Info from '../../../../assets/icons/info.svg?react';
import Insert from '../../../../assets/icons/insert.svg?react';
import Sun from '../../../../assets/icons/sun.svg?react';
import Moon from '../../../../assets/icons/moon.svg?react';
import Search from '../../../../assets/icons/search.svg?react';
import Delete from '../../../../assets/icons/delete.svg?react';
import Edit from '../../../../assets/icons/edit.svg?react';
import Close from '../../../../assets/icons/close.svg?react';
import Settings from '../../../../assets/icons/settings.svg?react';
import Repeat from '../../../../assets/icons/repeat.svg?react';
import ArrowDown from '../../../../assets/icons/arrowDown.svg?react';
import Quality from '../../../../assets/icons/quality.svg?react';
import Timeload from '../../../../assets/icons/timeload.svg?react';
import Passed from '../../../../assets/icons/passed.svg?react';
import Ava from '../../../../assets/icons/ava.svg?react';
import Menu from '../../../../assets/icons/menu.svg?react';
import MenuClose from '../../../../assets/icons/menuClose.svg?react';
import MoreMenu from '../../../../assets/icons/moreMenu.svg?react';
import Camera from '../../../../assets/icons/camera.svg?react';
import Copy from '../../../../assets/icons/copy.svg?react';
import Img from '../../../../assets/icons/img.svg?react';
import Pro from '../../../../assets/icons/pro.svg?react';
import Share from '../../../../assets/icons/share.svg?react';
import Play from '../../../../assets/icons/play.svg?react';
import ArrowCheck from '../../../../assets/icons/arrowCheck.svg?react';
import Check from '../../../../assets/icons/check.svg?react';
import Like from '../../../../assets/icons/like.svg?react';
import Star from '../../../../assets/icons/star.svg?react';
import Heart from '../../../../assets/icons/heart.svg?react';
import Dislike from '../../../../assets/icons/dislike.svg?react';
import Report from '../../../../assets/icons/report.svg?react';
import Ru from '../../../../assets/icons/lang/ru.svg?react';
import Eng from '../../../../assets/icons/lang/eng.svg?react';
import Insta from '../../../../assets/icons/social/insta.svg?react';
import VK from '../../../../assets/icons/social/vk.svg?react';
import Max from '../../../../assets/icons/social/max.svg?react';
import FB from '../../../../assets/icons/social/fb.svg?react';
import YouTube from '../../../../assets/icons/social/youtube.svg?react';
import Telegram from '../../../../assets/icons/social/telegram.svg?react';
import Twitter from '../../../../assets/icons/social/twitter.svg?react';
import Apple from '../../../../assets/icons/social/apple.svg?react';
import Google from '../../../../assets/icons/social/google.svg?react';
import Loader from '../../../../assets/icons/loader.svg?react';
import AppStore from '../../../../assets/icons/social/appstore.svg?react';
import GooglePlay from '../../../../assets/icons/social/googleplay.svg?react';
import WhatsApp from '../../../../assets/icons/social/whatsapp.svg?react';
import Reddit from '../../../../assets/icons/social/reddit.svg?react';
// Единый источник: добавляй иконку только сюда
export const components = {
    Plus, Minus, Next, Back, Open, Info, Insert, Search,
    Delete, Edit, Close, Settings, Repeat, ArrowDown, Quality,
    Timeload, Passed, Ava, Menu, MenuClose, MoreMenu, Camera, Copy,
    Pro, Share, Play, ArrowCheck, Check,
    Like, Star, Heart, Dislike, Report,
    Ru, Eng,
    Insta, VK, Max, FB, YouTube, Telegram, Twitter, Apple, Google, Loader,
    AppStore, GooglePlay, WhatsApp, Reddit, Sun, Moon, Img,
} satisfies Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>;

// Тип выводится автоматически из components
export type NameType = keyof typeof components;

/**
 * Цвет иконки.
 * Без color prop — иконка наследует CSS color от родителя (кнопка, текст).
 * 'default' — явный серый (semantic.surface.tertiary) для standalone-иконок.
 * 'muted'   — очень светлый серый (semantic.surface.secondary). Роль
 *             «low-emphasis индикатор», например иконка «В процессе» на
 *             карточке теста (см. `TestListItem.StatusSlot`) и в фильтре
 *             `StatusFilter` — одинаковый оттенок в обоих местах.
 */
export type IconColor = 'default' | 'primary' | 'secondary' | 'accent' | 'error' | 'white' | 'muted';

interface PropsType {
    name: NameType
    small?: boolean
    xSmall?: boolean
    size?: number | undefined
    autoSize?: boolean
    color?: IconColor
}

const Icon: React.FC<PropsType> = (props) => {
    const { name, ...wrapProps } = props;
    const Svg = components[name];

    return <Wrap {...wrapProps}><Svg /></Wrap>;
};

export default Icon;

const ICON_STYLE_PROPS = ['small', 'xSmall', 'size', 'autoSize', 'color'] as const;

const Wrap = styled.div.withConfig({
    shouldForwardProp: (prop) => !ICON_STYLE_PROPS.includes(prop as (typeof ICON_STYLE_PROPS)[number]),
})<Omit<PropsType, 'name'>>`
    width: 3rem;
    height: 3rem;
    display: inline-flex;
    vertical-align: middle;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;

    /* Без color prop — inherit (наследует от родителя: Button=white, текст=тёмный и т.д.) */
    /* С color prop — явное значение */
    ${({ color, theme }) => color && css`
        color: ${
            color === 'primary'   ? (theme.brand?.primary   ?? primitiveColors.primary[500])   :
            color === 'secondary' ? (theme.brand?.secondary ?? primitiveColors.secondary[500]) :
            color === 'accent'    ? (theme.brand?.accent    ?? primitiveColors.accent[500])    :
            color === 'error'     ? (theme.brand?.error     ?? primitiveColors.error[500])     :
            color === 'white'     ? primitiveColors.gray[50]                                   :
            color === 'muted'     ? (theme.semantic?.surface?.secondary ?? primitiveColors.gray[200]) :
            /* default */           (theme.semantic?.surface?.tertiary ?? primitiveColors.gray[400])
        };
    `}

    /* Монохромные классы — наследуют color через currentColor */
    & .colorFill    { fill:   currentColor; }
    & .themedIcon   { fill:   currentColor; }
    & .midtoneIcon  { fill:   currentColor; }
    & .colorStroke  { stroke: currentColor; }
    & .primaryStroke{ stroke: currentColor; }

    /* Явный color prop — перебивает CSS родителя (IconButton, Button вариантов) */
    ${({ color }) => color && css`
        && .colorFill    { fill:   currentColor !important; }
        && .themedIcon   { fill:   currentColor !important; }
        && .midtoneIcon  { fill:   currentColor !important; }
        && .colorStroke  { stroke: currentColor !important; }
        && .primaryStroke{ stroke: currentColor !important; }
    `}

    /* Прозрачный фон для составных иконок (Timeload, Passed) */
    & .itemBg { fill: transparent; }

    /* Фиксированные цвета для составных иконок */
    & .primary   { fill: ${(p) => p.theme.brand?.primary   ?? primitiveColors.primary[500]}; }
    & .accent    { fill: ${(p) => p.theme.brand?.accent    ?? primitiveColors.accent[500]}; }
    & .secondary { fill: ${(p) => p.theme.brand?.secondary ?? primitiveColors.secondary[500]}; }
    & .red       { fill: ${(p) => p.theme.brand?.error     ?? primitiveColors.error[500]}; }
    & .lightIcon { fill: ${(p) => p.theme.semantic.surface.secondary ?? primitiveColors.gray[200]}; }

    ${({ autoSize }) => autoSize && css`
        & {
            height: auto !important;
            width: auto !important;
        }
    `}
    
    ${({ size }) => size && css`
        & {
            height: ${size}rem;
            width: ${size}rem;
        }
    `}
    
    
    ${({ small }) => small && css`
        & svg {
            height: 2rem;
            width: 2rem;
        }

         & .colorStroke {
            stroke-width: 4px;
        }
    `}

     ${({ xSmall }) => xSmall && css`
        & svg {
            height: 1.3rem;
            width: 1.3rem;
        }

         & .colorStroke {
            stroke-width: 4px;
        }
    `}
`